import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doa } from '@/api/client';

/**
 * DoA e-sign — self-heal against DocuSeal directly, independent of
 * whether the webhook ever lands. Mirrors useDocusealSelfHeal.ts (eISF),
 * but DoA needed its OWN copy rather than a generalized/parametrized
 * version of that hook: DoA mints N+1 separate single-signer envelopes
 * per version (one per staff row + one for the PI) rather than one
 * envelope with named submitters, so its sync-status/finalize contract
 * and query-key shape are genuinely different, not just a different
 * endpoint path.
 *
 * Bug this fixes (2026-09-03 UAT: "DOA Logs: Signature issue not fixed
 * yet"): DoA was the ONLY signing module in this codebase with no
 * poll+finalize fallback — a row or PI countersignature that completed
 * in DocuSeal but whose webhook silently failed (or never fired) stayed
 * "Pending Signature" forever, with nothing to notice and retry. Polls
 * every 10s while `enabled` (matches DoaVersionDetail's own existing
 * in_review poll cadence); if it finds a completion Caspio doesn't know
 * about yet, calls the same finalize applier the webhook would call,
 * then invalidates the version query so the row table updates without a
 * manual reload.
 */
export function useDoaSignatureSelfHeal(binderId: string, versionId: string, enabled: boolean): void {
  const qc = useQueryClient();
  const finalizeTriggeredRef = useRef(false);

  const syncStatusQ = useQuery({
    queryKey: ['doa', 'sign-sync-status', versionId],
    queryFn: () => doa.getSignStatus(binderId, versionId),
    refetchInterval: 10_000,
    enabled,
  });

  const finalizeMut = useMutation({
    mutationFn: () => doa.finalizeSignatures(binderId, versionId),
    onSuccess: (r) => {
      if (r.synced) {
        // Invalidate both consumers — the grid editor (primary page,
        // queryKey ['doa-grid', binderId]) and the version detail page
        // (secondary page, queryKey ['binder', binderId, 'doa', 'version',
        // versionId]) — since either one may be the mounted caller of
        // this hook, or the other may be showing stale data in a
        // different tab.
        qc.invalidateQueries({ queryKey: ['doa-grid', binderId] });
        qc.invalidateQueries({ queryKey: ['binder', binderId, 'doa', 'version', versionId] });
      }
    },
  });

  useEffect(() => {
    if (!enabled) return;
    if (!syncStatusQ.data?.any_newly_completed) return;
    if (finalizeTriggeredRef.current || finalizeMut.isPending) return;
    finalizeTriggeredRef.current = true;
    finalizeMut.mutate(undefined, {
      onSettled: () => { finalizeTriggeredRef.current = false; },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, syncStatusQ.data]);
}
